import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import {
  IconButton,
  Paper,
  Popper,
  Fade,
  ClickAwayListener,
} from '@mui/material';

import createSvgIcon from 'shared/utils/createSvgIcon';

import TableFilterSelect, {
  type TableFilterSelectProps,
} from '../TableFilterSelect';

type StringOrNumber = string | number;

export type TableFilterProps<T extends StringOrNumber> =
  TableFilterSelectProps<T> & { selectedAll: boolean };

const FilterIcon = createSvgIcon(
  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />,
  '0 0 24 24'
);

const TableFilter = <T extends StringOrNumber>(props: TableFilterProps<T>) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const isActive = !props.selectedAll;

  return (
    <>
      <IconButton
        ref={anchorRef}
        sx={{
          padding: '3px',
          color: '#fff',
          ...(isActive && {
            backgroundColor: 'rgba(255,255,255,0.8)',
            color: '#5f92c7',

            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.8)',
              color: '#5f92c7',
            },
          }),
        }}
        onClick={handleToggle}
      >
        <FilterIcon sx={{ fontSize: '1.3rem', color: 'inherit' }} />
      </IconButton>
      <Popper
        anchorEl={anchorRef.current}
        placement="bottom-start"
        open={open}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            timeout={100}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper
              sx={{
                maxHeight: '200px',
                background: 'rgb(249, 249, 249)',
                borderRadius: 0,
                overflow: 'auto',
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <TableFilterSelect {...props} />
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default TableFilter;
